import { fmtDate } from '@/lib/dates.js';
import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { signatureQueue, doa, training, eisfDocumentHref, type SignatureQueueItem } from '@/api/client';
import { MultiSelectSearch, type MultiSelectOption } from '@/components/ui/MultiSelectSearch';
import { PageHeader } from '@/components/layout/PageHeader';
import { AlertTriangle, ChevronLeft, ChevronRight, FileSignature, Info, Loader2, Search, X } from 'lucide-react';
import { pollAndFinalizeTrainingSignature } from '@/lib/trainingSignaturePoll';
import { errorText, errorToneClass, isPermissionError } from '@/lib/permissionError';
import { SignVisitModal } from '@/pages/visits/VisitLifecyclePanel';

const PAGE_SIZE_OPTIONS = [10, 25, 50];

// Bug found live (UAT: "ESignature Queue... not listing 'training' and
// 'delegation' signs") — training and DoA/delegation each ran their own
// separate signing flow and never inserted a row into the shared queue
// at all. See services/caspio/signatureQueue.ts's SignatureQueueItemType.
const DOC_TYPE_OPTIONS: MultiSelectOption[] = [
  { value: 'econsent', label: 'eConsent' },
  { value: 'visit', label: 'Visit' },
  { value: 'training', label: 'Training' },
  // Both legs of the DoA ladder — filtering by 'doa' alone would hide
  // every PI countersignature, which is the item the PI most needs.
  { value: 'doa,doa_countersign', label: 'Delegation of Authority' },
  { value: 'eisf_approval', label: 'eISF document approval' },
];

export function SignatureQueuePage() {
  const qc = useQueryClient();
  const [subjectRef, setSubjectRef] = useState('');
  const [docTypes, setDocTypes] = useState<string[]>([]);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [signingDoaItem, setSigningDoaItem] = useState<SignatureQueueItem | null>(null);
  const [signingTrainingItem, setSigningTrainingItem] = useState<SignatureQueueItem | null>(null);
  // Visit sign-off (13 Oct 2026): the same dialog as the visit page's Sign Visit.
  const [signingVisitItem, setSigningVisitItem] = useState<SignatureQueueItem | null>(null);
  // Deep link from the dashboard's Approvals card: /signature-queue?open=<queue_item_id>
  // opens that item's signing dialog once the list has loaded, then drops
  // the param so a refresh doesn't reopen it.
  const [searchParams, setSearchParams] = useSearchParams();
  const openItemId = searchParams.get('open');

  // Reset to page 1 on any filter change.
  useEffect(() => { setPage(1); }, [subjectRef, docTypes, dateFrom, dateTo, pageSize]);

  const { data, isFetching, isError, error, refetch } = useQuery({
    queryKey: ['signature-queue', subjectRef, docTypes, dateFrom, dateTo, page, pageSize],
    queryFn: () =>
      signatureQueue.list({
        page,
        limit: pageSize,
        subject_ref: subjectRef.trim() || undefined,
        item_type: docTypes.length ? docTypes : undefined,
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined,
      }),
    staleTime: 0,
    refetchInterval: 30_000,
  });

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const hasMore = data?.hasMore ?? false;

  useEffect(() => {
    if (!openItemId || !data) return;
    const target = items.find((it) => it.queue_item_id === openItemId);
    if (target?.item_type === 'doa' || target?.item_type === 'doa_countersign') setSigningDoaItem(target);
    else if (target?.item_type === 'training') setSigningTrainingItem(target);
    else if (target?.item_type === 'visit') setSigningVisitItem(target);
    else if (!target) toast.info('That item is no longer in your queue — it may already be signed.');
    setSearchParams((prev) => { prev.delete('open'); return prev; }, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openItemId, data]);

  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = (page - 1) * pageSize + items.length;

  const hasActiveFilters = subjectRef.trim() || docTypes.length || dateFrom || dateTo;

  return (
    <div className="space-y-6">
      <PageHeader
        title="eSignature queue"
        subtitle="All documents and forms awaiting your signature at this site."
      />

      {/* Filters */}
      <div className="card p-4">
        <div className="grid gap-3 md:grid-cols-4">
          {/* Subject ref search */}
          <div>
            <label className="label text-xs">Subject ID</label>
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-ink-muted" />
              <input
                type="search"
                value={subjectRef}
                onChange={(e) => setSubjectRef(e.target.value)}
                placeholder="e.g. USA94-001 or SUB-1079"
                className="input w-full pl-8 text-sm"
              />
            </div>
          </div>

          {/* Document type multiselect */}
          <div>
            <label className="label text-xs">Document Type</label>
            <MultiSelectSearch
              options={DOC_TYPE_OPTIONS}
              value={docTypes}
              onChange={setDocTypes}
              placeholder="All types"
            />
          </div>

          {/* Date from */}
          <div>
            <label className="label text-xs">Date From</label>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              max={dateTo || undefined}
              className="input w-full text-sm"
            />
          </div>

          {/* Date to */}
          <div>
            <label className="label text-xs">Date To</label>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              min={dateFrom || undefined}
              className="input w-full text-sm"
            />
          </div>
        </div>

        {/* Refresh + clear row */}
        <div className="mt-3 flex items-center gap-2">
          <button
            type="button"
            className="btn-secondary"
            disabled={isFetching}
            onClick={() => refetch()}
          >
            {isFetching
              ? <Loader2 className="h-4 w-4 animate-spin" />
              : <Search className="h-4 w-4" />}
            {isFetching ? 'Loading…' : 'Refresh'}
          </button>
          {hasActiveFilters && (
            <button
              type="button"
              className="btn-ghost text-xs"
              onClick={() => {
                setSubjectRef('');
                setDocTypes([]);
                setDateFrom('');
                setDateTo('');
              }}
            >
              Clear filters
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="card overflow-hidden">
        {isFetching ? (
          <div className="flex items-center justify-center gap-2 p-8 text-ink-muted">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : isError ? (
          <div className={`flex items-start gap-2 p-4 text-sm ${errorToneClass(error)}`}>
            {isPermissionError(error) ? <Info className="mt-0.5 h-4 w-4 shrink-0"/> : <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />}
            <span>
              {errorText(error, 'Failed to load signature queue.')}
            </span>
          </div>
        ) : items.length === 0 ? (
          <div className="p-8 text-center text-sm text-ink-muted">
            <FileSignature className="mx-auto mb-2 h-6 w-6 opacity-50" />
            {hasActiveFilters
              ? 'No items match the filters.'
              : 'No documents are awaiting your signature.'}
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="border-b border-surface-border bg-surface-muted text-left text-xs uppercase tracking-wide text-ink-muted">
                  <tr>
                    <th className="px-3 py-2">Document / Form</th>
                    <th className="px-3 py-2">Version</th>
                    <th className="px-3 py-2">Subject / Reference</th>
                    <th className="px-3 py-2">Date Generated</th>
                    <th className="px-3 py-2 text-right">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((it) => (
                    <tr
                      key={it.queue_item_id}
                      className="border-b border-surface-border last:border-0 hover:bg-surface-muted/40"
                    >
                      <td className="px-3 py-3 text-xs font-medium text-ink-primary">
                        {it.document_name}
                      </td>
                      <td className="px-3 py-3 text-xs text-ink-secondary">
                        {it.document_version || '—'}
                      </td>
                      <td className="px-3 py-3 font-mono text-xs text-ink-secondary">
                        {it.subject_ref ??
                          (it.subject_id ? it.subject_id.slice(0, 8) : 'Site-level')}
                      </td>
                      <td className="px-3 py-3 text-xs">{fmtDate(it.generated_at)}</td>
                      <td className="px-3 py-3 text-right">
                        {it.item_type === 'doa' || it.item_type === 'doa_countersign' ? (
                          <button
                            type="button"
                            onClick={() => setSigningDoaItem(it)}
                            className="rounded bg-accent-600 px-3 py-1 text-xs font-medium text-white hover:bg-accent-700"
                          >
                            Sign Now
                          </button>
                        ) : it.item_type === 'training' ? (
                          <button
                            type="button"
                            onClick={() => setSigningTrainingItem(it)}
                            className="rounded bg-accent-600 px-3 py-1 text-xs font-medium text-white hover:bg-accent-700"
                          >
                            Sign Now
                          </button>
                        ) : it.item_type === 'eisf_approval' && it.eisf ? (
                          // Opens the binder at the document's folder with the
                          // document in the viewer, where the approver signs.
                          <Link
                            to={eisfDocumentHref(it.eisf)}
                            className="rounded bg-accent-600 px-3 py-1 text-xs font-medium text-white hover:bg-accent-700"
                          >
                            Sign Now
                          </Link>
                        ) : it.item_type === 'visit' ? (
                          <span className="inline-flex items-center gap-1.5">
                            <Link
                              to={`/visits/${encodeURIComponent(it.resource_id)}`}
                              className="rounded border border-surface-border px-3 py-1 text-xs font-medium hover:bg-surface-muted/50"
                              title="Review the visit's forms before signing"
                            >
                              Review
                            </Link>
                            <button
                              type="button"
                              onClick={() => setSigningVisitItem(it)}
                              className="rounded bg-accent-600 px-3 py-1 text-xs font-medium text-white hover:bg-accent-700"
                            >
                              Sign Now
                            </button>
                          </span>
                        ) : (
                          // Bug found live (24 Sep 2026): target="_blank" here
                          // opened these same-origin app routes in a new tab,
                          // which immediately bounced to /login — the session
                          // lives in sessionStorage, never inherited by a new
                          // tab (rel="noopener" blocks the same-origin clone
                          // Chrome would otherwise do). Same-tab navigation
                          // fixes it, matching the 'doa' branch's in-page modal.
                          <Link
                            to={
                              it.item_type === 'econsent'
                                ? `/consent/staff-sign?cr=${encodeURIComponent(it.resource_id)}`
                                : `/signature-queue?open=${encodeURIComponent(it.queue_item_id)}`
                            }
                            className={
                              it.requires_review
                                ? 'rounded border border-surface-border px-3 py-1 text-xs font-medium hover:bg-surface-muted/50'
                                : 'rounded bg-accent-600 px-3 py-1 text-xs font-medium text-white hover:bg-accent-700'
                            }
                          >
                            {it.requires_review ? 'Review' : 'Sign Now'}
                          </Link>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination footer — same pattern as SubjectsPage */}
            <div className="flex flex-wrap items-center justify-between gap-3 border-t border-surface-border px-4 py-3 text-xs text-ink-muted">
              <div className="flex items-center gap-3">
                {!hasMore
                  ? <span>Showing {from}–{to} of <strong className="text-ink-primary">{total}</strong> {total === 1 ? 'item' : 'items'}</span>
                  : <span>Page {page} · {items.length} rows</span>}
                <div className="flex items-center gap-1.5">
                  <span>Rows per page:</span>
                  <select
                    className="input py-0.5 text-xs w-16"
                    value={pageSize}
                    onChange={(e) => setPageSize(Number(e.target.value))}
                  >
                    {PAGE_SIZE_OPTIONS.map((n) => (
                      <option key={n} value={n}>{n}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="flex gap-1">
                <button
                  type="button"
                  className="btn-secondary py-1 px-2 text-xs disabled:opacity-40"
                  disabled={page <= 1}
                  onClick={() => setPage(page - 1)}
                >
                  <ChevronLeft className="h-3 w-3" /> Prev
                </button>
                <button
                  type="button"
                  className="btn-secondary py-1 px-2 text-xs disabled:opacity-40"
                  disabled={!hasMore}
                  onClick={() => setPage(page + 1)}
                >
                  Next <ChevronRight className="h-3 w-3" />
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {signingDoaItem && (
        <DoaSignDialog item={signingDoaItem} onClose={() => setSigningDoaItem(null)} />
      )}

      {signingTrainingItem && (
        <TrainingSignDialog item={signingTrainingItem} onClose={() => setSigningTrainingItem(null)} />
      )}
      {signingVisitItem && (
        <SignVisitModal
          visitId={signingVisitItem.resource_id}
          onClose={() => setSigningVisitItem(null)}
          onSigned={() => {
            setSigningVisitItem(null);
            qc.invalidateQueries({ queryKey: ['signature-queue'] });
            qc.invalidateQueries({ queryKey: ['visit', 'sign-history', signingVisitItem.resource_id] });
            qc.invalidateQueries({ queryKey: ['visit', signingVisitItem.resource_id] });
          }}
        />
      )}
    </div>
  );
}

/**
 * Bug found live (2026-09-04): the queue's "Sign Now" for a DoA item used
 * to route to a generic /binder list page — there was no authenticated
 * way to fetch a DoA row/version's embed URL (the existing signing
 * endpoints are all public, PIN/OTP-gated routes built for the
 * unauthenticated emailed-link flow). This opens the real DocuSeal embed
 * directly, same in-app-iframe pattern as Training's sign dialog.
 *
 * No check-once-on-close finalize here (unlike Training's dialog) — DoA's
 * OWN self-heal already covers this from two other angles (the grid/
 * detail pages' own poll, and the real webhook), and this dialog doesn't
 * have a great "wait for it" signal of its own without duplicating that
 * polling. It does still call finalize ONCE right when the user closes,
 * as a fast-path so the queue item doesn't linger for up to 30s waiting
 * on the page's own poll.
 */
function DoaSignDialog({ item, onClose }: { item: SignatureQueueItem; onClose: () => void }) {
  const qc = useQueryClient();
  const [embedUrl, setEmbedUrl] = useState<string | null>(null);
  const [binderId, setBinderId] = useState<string | null>(null);
  const [doaVersionId, setDoaVersionId] = useState<string | null>(null);
  const [starting, setStarting] = useState(true);
  const [startError, setStartError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setStarting(true);
    setStartError(null);
    doa.getSignEmbed(item.resource_id)
      .then((r) => {
        if (cancelled) return;
        setEmbedUrl(r.embed_url);
        setBinderId(r.binder_id);
        setDoaVersionId(r.doa_version_id);
      })
      .catch((e) => {
        if (!cancelled) setStartError(errorText(e, 'Failed to start signing'));
      })
      .finally(() => {
        if (!cancelled) setStarting(false);
      });
    return () => { cancelled = true; };
  }, [item.resource_id]);

  const finalizeMut = useMutation({
    mutationFn: () => {
      if (!binderId || !doaVersionId) throw new Error('Missing binder/version id');
      return doa.finalizeSignatures(binderId, doaVersionId);
    },
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['signature-queue'] });
      if (r.synced) toast.success('Signature recorded.');
    },
  });

  function handleCloseClick() {
    if (finalizeMut.isPending) return;
    if (!embedUrl) { onClose(); return; }
    finalizeMut.mutate(undefined, { onSettled: onClose });
  }

  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center bg-ink-primary/60 p-4 !m-0">
      <div className="flex h-[85vh] w-full max-w-4xl flex-col overflow-hidden rounded-lg bg-white shadow-2xl">
        <div className="flex shrink-0 items-center justify-between border-b border-surface-border px-5 py-3">
          <div>
            <div className="text-sm font-semibold text-ink-primary">Sign delegation</div>
            <div className="mt-0.5 text-[11px] text-ink-muted">{item.document_name}</div>
          </div>
          <button
            type="button"
            onClick={handleCloseClick}
            disabled={finalizeMut.isPending}
            className="rounded p-1 text-ink-muted hover:bg-surface-muted/40 hover:text-ink-primary disabled:cursor-not-allowed disabled:opacity-40"
            title={finalizeMut.isPending ? 'Finishing up…' : 'Close'}
          >
            {finalizeMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <X className="h-4 w-4" />}
          </button>
        </div>

        <div className="flex flex-1 flex-col overflow-hidden">
          {starting && (
            <div className="flex flex-1 items-center justify-center text-xs text-ink-muted">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Preparing signing session…
            </div>
          )}

          {startError && (
            <div className="flex flex-1 items-center justify-center p-6">
              <div className="max-w-sm rounded border border-danger-200 bg-danger-50 p-4 text-center text-xs text-danger-700">
                {startError}
              </div>
            </div>
          )}

          {!starting && !startError && embedUrl && (
            <iframe
              title="DoA signature"
              src={embedUrl}
              className="w-full flex-1 border-0"
              allow="camera; microphone; clipboard-read; clipboard-write"
            />
          )}
        </div>
      </div>
    </div>
  );
}

function TrainingSignDialog({ item, onClose }: { item: SignatureQueueItem; onClose: () => void }) {
  const qc = useQueryClient();
  const [embedUrl, setEmbedUrl] = useState<string | null>(null);
  const [starting, setStarting] = useState(true);
  const [startError, setStartError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setStarting(true);
    setStartError(null);
    training.startCountersigning(item.resource_id)
      .then((r) => {
        if (cancelled) return;
        setEmbedUrl(r.embed_url);
      })
      .catch((e) => {
        if (!cancelled) setStartError(errorText(e, 'Failed to start signing'));
      })
      .finally(() => {
        if (!cancelled) setStarting(false);
      });
    return () => { cancelled = true; };
  }, [item.resource_id]);

  const finalizeMut = useMutation({
    // Same poll-then-finalize as the Training page dialog: DocuSeal flags
    // the submitter complete a beat after the iframe shows "done", and the
    // write only happens when something new is actually there.
    mutationFn: () => pollAndFinalizeTrainingSignature(item.resource_id),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['signature-queue'] });
      qc.invalidateQueries({ queryKey: ['training'] });
      if (r.synced) toast.success('Countersignature recorded.');
    },
  });

  function handleCloseClick() {
    if (finalizeMut.isPending) return;
    if (!embedUrl) { onClose(); return; }
    finalizeMut.mutate(undefined, { onSettled: onClose });
  }

  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center bg-ink-primary/60 p-4 !m-0">
      <div className="flex h-[85vh] w-full max-w-4xl flex-col overflow-hidden rounded-lg bg-white shadow-2xl">
        <div className="flex shrink-0 items-center justify-between border-b border-surface-border px-5 py-3">
          <div>
            <div className="text-sm font-semibold text-ink-primary">Countersign training</div>
            <div className="mt-0.5 text-[11px] text-ink-muted">{item.document_name}</div>
          </div>
          <button
            type="button"
            onClick={handleCloseClick}
            disabled={finalizeMut.isPending}
            className="rounded p-1 text-ink-muted hover:bg-surface-muted/40 hover:text-ink-primary disabled:cursor-not-allowed disabled:opacity-40"
            title={finalizeMut.isPending ? 'Finishing up…' : 'Close'}
          >
            {finalizeMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <X className="h-4 w-4" />}
          </button>
        </div>

        <div className="flex flex-1 flex-col overflow-hidden">
          {starting && (
            <div className="flex flex-1 items-center justify-center text-xs text-ink-muted">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Preparing signing session…
            </div>
          )}

          {startError && (
            <div className="flex flex-1 items-center justify-center p-6">
              <div className="max-w-sm rounded border border-danger-200 bg-danger-50 p-4 text-center text-xs text-danger-700">
                {startError}
              </div>
            </div>
          )}

          {!starting && !startError && embedUrl && (
            <iframe
              title="Training signature"
              src={embedUrl}
              className="w-full flex-1 border-0"
              allow="camera; microphone; clipboard-read; clipboard-write"
            />
          )}
        </div>
      </div>
    </div>
  );
}
